/**
 * Remaining Services Content
 * GST, Tax Planning, Licenses, and more
 */

export const gstRegistration = {
  id: "gst-registration",
  slug: "gst-registration",
  seoTitle: "GST Registration Online in India | Expert Registration Services",
  seoDescription: "Get GST Registration online with expert assistance. Quick processing, complete documentation support, and compliance guidance. Start your business legally.",
  
  heroTitle: "GST Registration",
  heroSubtitle: "Tax Registration",
  
  summary: "GST (Goods and Services Tax) Registration is mandatory for businesses with annual turnover exceeding Rs. 40 lakhs (Rs. 20 lakhs for service providers). A GSTIN enables you to collect GST from customers, claim input tax credits, and comply with India's unified indirect tax system. Our experts handle the complete registration process, ensuring accurate filing and quick approval.",
  
  whoNeeds: {
    title: "Who Needs GST Registration?",
    description: "GST registration is mandatory for various business categories and optional for others seeking compliance benefits.",
    points: [
      "Businesses with annual turnover exceeding Rs. 40 lakhs (goods)",
      "Service providers with turnover exceeding Rs. 20 lakhs",
      "Inter-state suppliers regardless of turnover",
      "E-commerce sellers and operators",
      "Businesses making taxable supplies",
      "Agents and input service distributors",
      "Casual taxable persons and NRIs doing business",
      "Those wanting to claim input tax credits"
    ]
  },
  
  whyRequired: {
    title: "Why is GST Registration Important?",
    description: "GST registration provides legal authorization to collect tax and enjoy various business benefits.",
    points: [
      "Legal authorization to collect GST from customers",
      "Claim input tax credit on purchases",
      "Expand business through inter-state sales",
      "Sell on e-commerce platforms (Amazon, Flipkart)",
      "Enhanced business credibility",
      "Access to government tenders requiring GSTIN",
      "Avoid penalties for non-registration",
      "Build compliant business operations"
    ]
  },
  
  benefits: [
    {
      title: "Input Tax Credit",
      description: "Claim credit on GST paid on purchases, reducing overall tax liability"
    },
    {
      title: "Inter-State Sales",
      description: "Legally sell goods and services across state borders with proper taxation"
    },
    {
      title: "E-Commerce Selling",
      description: "Mandatory for selling on platforms like Amazon, Flipkart, Myntra"
    },
    {
      title: "Business Credibility",
      description: "GSTIN establishes legitimacy and builds trust with customers and vendors"
    },
    {
      title: "Government Contracts",
      description: "GST registration often required for government tenders and contracts"
    },
    {
      title: "Composition Scheme",
      description: "Small businesses can opt for simplified taxation with composition scheme"
    }
  ],
  
  documentsRequired: [
    {
      category: "Proprietorship",
      items: [
        "PAN Card of proprietor",
        "Aadhaar Card",
        "Passport-size photograph",
        "Bank account statement/cancelled cheque",
        "Business address proof"
      ]
    },
    {
      category: "Partnership/LLP/Company",
      items: [
        "PAN Card of the business",
        "Partnership deed/LLP Agreement/COI",
        "PAN and Aadhaar of all partners/directors",
        "Board resolution (for companies)",
        "Digital signature of authorized signatory"
      ]
    },
    {
      category: "Address Proof",
      items: [
        "Electricity bill (not older than 2 months)",
        "Rent agreement (if rented)",
        "NOC from property owner",
        "Property tax receipt (if owned)",
        "Legal possession document"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Document Collection",
      description: "Gather all required documents including identity proof, address proof, and business registration documents."
    },
    {
      step: 2,
      title: "Application Preparation",
      description: "Prepare GST registration application (Form REG-01) with accurate business and promoter details."
    },
    {
      step: 3,
      title: "Portal Submission",
      description: "Submit the application on GST portal with all supporting documents and digital signature."
    },
    {
      step: 4,
      title: "ARN Generation",
      description: "Receive Application Reference Number (ARN) for tracking the registration status."
    },
    {
      step: 5,
      title: "Verification",
      description: "GST officer verifies the application. Respond to any queries or additional document requests."
    },
    {
      step: 6,
      title: "GSTIN Issuance",
      description: "Upon approval, receive 15-digit GSTIN and GST registration certificate."
    }
  ],
  
  timeline: {
    total: "3-7 working days",
    breakdown: [
      { phase: "Document Preparation", duration: "1 day" },
      { phase: "Application Filing", duration: "1 day" },
      { phase: "Department Processing", duration: "2-5 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹1,499",
    includes: [
      "Complete application filing",
      "Document verification",
      "GST portal registration",
      "Query handling",
      "GSTIN certificate",
      "Post-registration guidance"
    ],
    governmentFees: "Nil (No government fee)"
  },
  
  faqs: [
    {
      question: "What is the threshold limit for GST registration?",
      answer: "Rs. 40 lakhs for goods sellers (Rs. 20 lakhs for NE states) and Rs. 20 lakhs for service providers (Rs. 10 lakhs for NE states). Inter-state suppliers must register regardless of turnover."
    },
    {
      question: "How long does GST registration take?",
      answer: "Typically 3-7 working days if all documents are in order. Physical verification may add a few days in some cases."
    },
    {
      question: "What is GSTIN?",
      answer: "GSTIN is a 15-digit alphanumeric Goods and Services Tax Identification Number assigned to registered taxpayers. It's used for all GST-related transactions and filings."
    },
    {
      question: "Can I register voluntarily below threshold?",
      answer: "Yes, voluntary registration is allowed even below threshold limits. This is beneficial for claiming input tax credits and building business credibility."
    },
    {
      question: "What are the penalties for not registering?",
      answer: "Penalty of Rs. 10,000 or the tax amount due, whichever is higher. Additionally, you cannot collect GST or claim input credits without registration."
    },
    {
      question: "Do I need separate registration for each state?",
      answer: "Yes, you need separate GST registration in each state where you have a place of business. One GSTIN per state."
    }
  ],
  
  relatedServices: [
    { name: "GST Return Filing", slug: "gst-return-filing" },
    { name: "GST Annual Return", slug: "gst-annual-return" },
    { name: "GST LUT Filing", slug: "gst-lut-filing" },
    { name: "E-Way Bill", slug: "e-way-bill" }
  ],
  
  trustSignals: {
    experience: "8+ Years GST Expertise",
    experts: "CA & Tax Professionals",
    registrations: "10,000+ GST Registrations",
    support: "Free Consultation"
  }
};

export const eWayBill = {
  id: "e-way-bill",
  slug: "e-way-bill",
  seoTitle: "E-Way Bill Generation Services | GST Transport Documentation",
  seoDescription: "Expert E-Way Bill generation for goods movement. Avoid penalties and ensure smooth transportation with compliant e-way bill documentation.",
  
  heroTitle: "E-Way Bill Services",
  heroSubtitle: "GST Compliance",
  
  summary: "E-Way Bill is an electronic document required for movement of goods worth more than Rs. 50,000 under GST. It must be generated before goods are transported and is verified at check posts. Our services help businesses generate accurate e-way bills, manage bulk generations, and ensure compliance to avoid penalties and goods detention.",
  
  whoNeeds: {
    title: "Who Needs E-Way Bill?",
    description: "E-way bills are mandatory for various movement scenarios under GST.",
    points: [
      "Suppliers moving goods worth over Rs. 50,000",
      "Transporters carrying consignments",
      "E-commerce operators and sellers",
      "Job work goods movement",
      "Inter-state and intra-state transport",
      "Sales returns and goods rejection",
      "Exhibition and event goods movement",
      "Warehouse to warehouse transfers"
    ]
  },
  
  whyRequired: {
    title: "Why is E-Way Bill Compliance Important?",
    description: "E-way bill compliance is critical for smooth transportation and avoiding penalties.",
    points: [
      "Mandatory for goods movement over Rs. 50,000",
      "Avoid goods detention and seizure",
      "Prevent heavy penalties and fines",
      "Ensure smooth inter-state movement",
      "Track and trace goods movement",
      "Maintain proper documentation",
      "Build transporter relationships",
      "Legal compliance requirements"
    ]
  },
  
  benefits: [
    {
      title: "Avoid Detention",
      description: "Proper e-way bills prevent goods detention at check posts and during transit"
    },
    {
      title: "Penalty Prevention",
      description: "Avoid penalties up to 200% of tax amount for non-compliance"
    },
    {
      title: "Smooth Operations",
      description: "Ensure hassle-free goods movement and timely deliveries"
    },
    {
      title: "Digital Records",
      description: "Maintain digital trail of all goods movements for audit purposes"
    },
    {
      title: "Part Load Management",
      description: "Generate consolidated e-way bills for multiple consignments"
    },
    {
      title: "Extension Support",
      description: "Extend e-way bill validity when goods are in transit"
    }
  ],
  
  documentsRequired: [
    {
      category: "Transaction Documents",
      items: [
        "Tax invoice or bill of supply",
        "Delivery challan (for non-supply)",
        "Bill of entry (for imports)",
        "Transporter ID and vehicle number",
        "GSTIN of supplier and recipient"
      ]
    },
    {
      category: "Goods Details",
      items: [
        "HSN code of goods",
        "Description of goods",
        "Quantity and unit",
        "Taxable value and tax amount",
        "Approximate distance of transport"
      ]
    },
    {
      category: "Transport Details",
      items: [
        "Mode of transport (road/rail/air/ship)",
        "Vehicle number (for road transport)",
        "Transporter name and GSTIN",
        "LR/RR number if applicable",
        "Place of loading and delivery"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Invoice Preparation",
      description: "Prepare proper tax invoice or delivery challan with all required details including HSN codes."
    },
    {
      step: 2,
      title: "Portal Login",
      description: "Access the E-Way Bill portal with registered GST credentials."
    },
    {
      step: 3,
      title: "Form Filling",
      description: "Enter transaction details, goods information, and transport particulars in Form EWB-01."
    },
    {
      step: 4,
      title: "E-Way Bill Generation",
      description: "Submit and generate unique 12-digit E-Way Bill Number (EBN)."
    },
    {
      step: 5,
      title: "Share with Transporter",
      description: "Share EBN with transporter who can update vehicle details if required."
    },
    {
      step: 6,
      title: "Validity Management",
      description: "Track validity and extend if goods are in transit beyond valid period."
    }
  ],
  
  timeline: {
    total: "Instant to 30 minutes",
    breakdown: [
      { phase: "Single E-Way Bill", duration: "5-10 minutes" },
      { phase: "Bulk Generation", duration: "15-30 minutes" },
      { phase: "Consolidated Bill", duration: "10-15 minutes" }
    ]
  },
  
  pricing: {
    startingFrom: "₹99 per bill",
    includes: [
      "E-way bill generation",
      "Vehicle update support",
      "Extension assistance",
      "Consolidated e-way bills",
      "Rejection/cancellation handling",
      "Query resolution"
    ],
    governmentFees: "Nil (No government fee)"
  },
  
  faqs: [
    {
      question: "What is the validity of an e-way bill?",
      answer: "For regular vehicles: 1 day per 100 km (or part thereof). For over-dimensional cargo: 1 day per 20 km. Validity starts from date and time of generation."
    },
    {
      question: "When is e-way bill not required?",
      answer: "Not required when goods value is below Rs. 50,000, for exempted goods, for goods transported by non-motorized conveyance, or for specific notified goods and situations."
    },
    {
      question: "What is the penalty for not having e-way bill?",
      answer: "Penalty of Rs. 10,000 or tax evaded, whichever is higher. Goods and vehicle can be detained until penalty is paid."
    },
    {
      question: "Can e-way bill be cancelled?",
      answer: "Yes, e-way bill can be cancelled within 24 hours of generation if goods are not transported. Once verified by an officer, it cannot be cancelled."
    },
    {
      question: "Can transporter generate e-way bill?",
      answer: "Yes, if the consignor doesn't generate, the transporter can generate Part A, and Part B (vehicle details) can be updated by either party."
    },
    {
      question: "Is e-way bill required for intra-state movement?",
      answer: "Yes, most states require e-way bill for intra-state movement over Rs. 50,000. Some states have different thresholds, check state-specific rules."
    }
  ],
  
  relatedServices: [
    { name: "GST Registration", slug: "gst-registration" },
    { name: "GST Return Filing", slug: "gst-return-filing" },
    { name: "GST LUT Filing", slug: "gst-lut-filing" },
    { name: "IEC Code", slug: "iec-code" }
  ],
  
  trustSignals: {
    experience: "5+ Years E-Way Bill Expertise",
    experts: "GST Compliance Specialists",
    bills: "50,000+ E-Way Bills Generated",
    support: "24/7 Support Available"
  }
};

export const taxPlanning = {
  id: "tax-planning",
  slug: "tax-planning",
  seoTitle: "Strategic Tax Planning Services | Expert Tax Optimization",
  seoDescription: "Professional tax planning services to legally minimize your tax liability. Expert guidance on deductions, exemptions, and investment strategies.",
  
  heroTitle: "Strategic Tax Planning",
  heroSubtitle: "Tax Optimization",
  
  summary: "Strategic tax planning is the legal method of analyzing your financial situation to minimize tax liability while achieving financial goals. Our expert team provides comprehensive tax planning services including investment advice, deduction optimization, business structure analysis, and year-round tax strategy. We help individuals and businesses pay only what they legally owe while maximizing wealth creation.",
  
  whoNeeds: {
    title: "Who Needs Tax Planning Services?",
    description: "Tax planning benefits anyone looking to optimize their tax position legally.",
    points: [
      "High-income individuals and families",
      "Business owners and entrepreneurs",
      "Professionals with multiple income sources",
      "Investors with capital gains exposure",
      "NRIs with India income or investments",
      "Startups planning equity compensation",
      "Individuals approaching retirement",
      "Those receiving bonuses or variable pay"
    ]
  },
  
  whyRequired: {
    title: "Why is Tax Planning Important?",
    description: "Proactive tax planning provides significant financial advantages.",
    points: [
      "Legally reduce tax liability",
      "Maximize investment returns",
      "Plan business structure efficiently",
      "Utilize all available deductions",
      "Avoid last-minute tax decisions",
      "Build long-term wealth effectively",
      "Navigate complex tax regulations",
      "Achieve financial goals faster"
    ]
  },
  
  benefits: [
    {
      title: "Tax Savings",
      description: "Identify all legitimate deductions and exemptions to reduce tax outgo"
    },
    {
      title: "Investment Optimization",
      description: "Choose tax-efficient investment options aligned with financial goals"
    },
    {
      title: "Cash Flow Management",
      description: "Plan advance tax payments to optimize cash flow throughout the year"
    },
    {
      title: "Compliance Assurance",
      description: "Stay compliant while maximizing benefits under tax laws"
    },
    {
      title: "Business Structuring",
      description: "Optimize business structure for tax efficiency"
    },
    {
      title: "Future Planning",
      description: "Plan for major life events with tax implications in mind"
    }
  ],
  
  documentsRequired: [
    {
      category: "Income Documents",
      items: [
        "Salary slips and Form 16",
        "Bank statements showing interest income",
        "Investment statements",
        "Business income details",
        "Rental income documents"
      ]
    },
    {
      category: "Investment Documents",
      items: [
        "Insurance premium receipts",
        "PPF/ELSS/NPS statements",
        "Home loan statements",
        "Education loan certificates",
        "Medical insurance premium receipts"
      ]
    },
    {
      category: "Business Documents (if applicable)",
      items: [
        "Profit & Loss statement",
        "Balance Sheet",
        "Business expense records",
        "Depreciation schedules",
        "GST returns"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Financial Assessment",
      description: "Comprehensive review of your income sources, investments, expenses, and current tax position."
    },
    {
      step: 2,
      title: "Tax Position Analysis",
      description: "Calculate current tax liability and identify optimization opportunities under various sections."
    },
    {
      step: 3,
      title: "Strategy Development",
      description: "Develop customized tax planning strategy based on your financial goals and risk profile."
    },
    {
      step: 4,
      title: "Investment Recommendations",
      description: "Recommend tax-saving investments (80C, 80D, 80CCD, etc.) aligned with your needs."
    },
    {
      step: 5,
      title: "Implementation Support",
      description: "Assist in implementing the tax plan including investment documentation and timing."
    },
    {
      step: 6,
      title: "Year-Round Monitoring",
      description: "Regular reviews and adjustments based on income changes and new regulations."
    }
  ],
  
  timeline: {
    total: "Ongoing engagement",
    breakdown: [
      { phase: "Initial Assessment", duration: "3-5 days" },
      { phase: "Strategy Development", duration: "5-7 days" },
      { phase: "Implementation", duration: "Ongoing" },
      { phase: "Quarterly Reviews", duration: "Ongoing" }
    ]
  },
  
  pricing: {
    startingFrom: "₹7,999",
    includes: [
      "Comprehensive tax assessment",
      "Customized planning strategy",
      "Investment recommendations",
      "Deduction optimization",
      "Quarterly review calls",
      "Year-end planning support"
    ],
    governmentFees: "N/A"
  },
  
  faqs: [
    {
      question: "What's the difference between tax planning and tax evasion?",
      answer: "Tax planning is legal use of provisions in tax law to minimize liability. Tax evasion is illegal concealment of income or false claims. We only provide legitimate tax planning strategies."
    },
    {
      question: "When should I start tax planning?",
      answer: "Ideally at the beginning of the financial year (April). However, it's never too late. Year-round planning is more effective than last-minute decisions in February-March."
    },
    {
      question: "What are the main tax-saving sections?",
      answer: "Section 80C (Rs. 1.5 lakh), 80D (health insurance), 80CCD(1B) (NPS), 80E (education loan), 80G (donations), 80TTA/80TTB (interest), and HRA/LTA exemptions are key areas."
    },
    {
      question: "Can tax planning help with capital gains?",
      answer: "Yes, through strategies like indexation, holding period optimization, reinvestment exemptions (54, 54EC, 54F), and timing of sales for tax-efficient exits."
    },
    {
      question: "Do you help with advance tax planning?",
      answer: "Yes, we help plan advance tax payments to avoid interest (234B, 234C) while optimizing cash flow. We provide due date reminders and calculation support."
    },
    {
      question: "Is tax planning useful for businesses?",
      answer: "Absolutely. Business tax planning includes choosing the right structure, maximizing allowable expenses, depreciation planning, and utilizing available incentives."
    }
  ],
  
  relatedServices: [
    { name: "Income Tax Filing", slug: "income-tax-filing" },
    { name: "Virtual CFO", slug: "virtual-cfo" },
    { name: "GST Return Filing", slug: "gst-return-filing" },
    { name: "TDS Filing", slug: "tds-filing" }
  ],
  
  trustSignals: {
    experience: "15+ Years Tax Expertise",
    experts: "CA & Tax Professionals",
    clients: "2,000+ Clients Served",
    support: "Year-Round Advisory"
  }
};

export const companyWindingUp = {
  id: "company-winding-up",
  slug: "company-winding-up",
  seoTitle: "Company Winding Up Services | Formal Company Closure",
  seoDescription: "Expert company winding up services through NCLT. Complete guidance for voluntary and compulsory winding up proceedings. Professional liquidation support.",
  
  heroTitle: "Company Winding Up",
  heroSubtitle: "Company Closure",
  
  summary: "Company Winding Up is the formal process of bringing a company's existence to an end, settling all liabilities, distributing assets, and ultimately dissolving the company. Unlike Strike Off (for dormant companies), winding up is used when a company has assets, liabilities, or legal obligations that need proper settlement. Our experts guide you through voluntary liquidation or NCLT proceedings.",
  
  whoNeeds: {
    title: "Who Needs Company Winding Up?",
    description: "Winding up is appropriate for companies with assets, liabilities, or those unable to use strike off.",
    points: [
      "Companies with significant assets requiring liquidation",
      "Businesses unable to pay debts (compulsory winding up)",
      "Companies not qualifying for strike off",
      "Creditor-initiated company closures",
      "Court-ordered dissolutions",
      "Promoters wanting formal asset distribution",
      "Companies with ongoing litigations",
      "Businesses with regulatory issues preventing strike off"
    ]
  },
  
  whyRequired: {
    title: "Why Choose Formal Winding Up?",
    description: "Formal winding up provides legal protection and proper closure for complex situations.",
    points: [
      "Proper settlement of all liabilities",
      "Legal protection for directors and promoters",
      "Systematic asset liquidation and distribution",
      "Binding effect on creditors after process",
      "Court supervision ensures fairness",
      "Clear closure of all company affairs",
      "Protection against future claims",
      "Proper documentation for regulatory compliance"
    ]
  },
  
  benefits: [
    {
      title: "Complete Closure",
      description: "All company affairs properly wound up with legal finality"
    },
    {
      title: "Asset Distribution",
      description: "Systematic liquidation and fair distribution among stakeholders"
    },
    {
      title: "Creditor Settlement",
      description: "Proper process for settling outstanding creditor claims"
    },
    {
      title: "Director Protection",
      description: "Legal protection for directors through proper winding up"
    },
    {
      title: "Court Supervision",
      description: "NCLT oversight ensures transparent and fair process"
    },
    {
      title: "Regulatory Compliance",
      description: "Complete compliance with Companies Act requirements"
    }
  ],
  
  documentsRequired: [
    {
      category: "Company Documents",
      items: [
        "Certificate of Incorporation",
        "MOA and AOA",
        "Audited financial statements (3 years)",
        "Board resolutions for winding up",
        "Statement of affairs"
      ]
    },
    {
      category: "Financial Documents",
      items: [
        "List of creditors and amounts",
        "List of debtors and amounts",
        "Details of all assets",
        "Bank statements",
        "Pending litigations details"
      ]
    },
    {
      category: "Compliance Documents",
      items: [
        "All ROC filings record",
        "Tax returns and assessments",
        "GST compliance records",
        "Employee settlement details",
        "Statutory dues clearance"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Initial Assessment",
      description: "Review company's financial position, assets, liabilities, and determine appropriate winding up route (voluntary or compulsory)."
    },
    {
      step: 2,
      title: "Board & Shareholder Approval",
      description: "Pass board resolution and special resolution in general meeting approving the winding up."
    },
    {
      step: 3,
      title: "Declaration of Solvency",
      description: "Directors declare solvency (for voluntary winding up) with statement of assets and liabilities."
    },
    {
      step: 4,
      title: "Appoint Liquidator",
      description: "Appoint Company Liquidator to take charge of winding up proceedings."
    },
    {
      step: 5,
      title: "NCLT Application",
      description: "File application with National Company Law Tribunal with required documents."
    },
    {
      step: 6,
      title: "Asset Liquidation",
      description: "Liquidator realizes assets, collects dues, and prepares for distribution."
    },
    {
      step: 7,
      title: "Creditor Settlement",
      description: "Settle all creditor claims in order of priority prescribed under law."
    },
    {
      step: 8,
      title: "Final Distribution",
      description: "Distribute remaining assets to shareholders and apply for dissolution order."
    }
  ],
  
  timeline: {
    total: "6-18 months",
    breakdown: [
      { phase: "Preparation & Filing", duration: "1-2 months" },
      { phase: "NCLT Proceedings", duration: "3-6 months" },
      { phase: "Liquidation Process", duration: "3-9 months" },
      { phase: "Final Dissolution", duration: "1-2 months" }
    ]
  },
  
  pricing: {
    startingFrom: "₹99,999",
    includes: [
      "Complete winding up filing",
      "NCLT application preparation",
      "Liquidator coordination",
      "Creditor correspondence",
      "Asset distribution support",
      "Dissolution application"
    ],
    governmentFees: "Additional court fees apply"
  },
  
  faqs: [
    {
      question: "What's the difference between Strike Off and Winding Up?",
      answer: "Strike Off is simpler, for dormant companies with no assets/liabilities. Winding Up is formal liquidation through NCLT for companies with assets, liabilities, or those not qualifying for strike off."
    },
    {
      question: "What are the types of winding up?",
      answer: "Two types: (1) Voluntary Winding Up - initiated by company when solvent, and (2) Compulsory Winding Up - by NCLT order on creditor/company petition when unable to pay debts."
    },
    {
      question: "Who can be appointed as Liquidator?",
      answer: "A registered Insolvency Professional or any person approved by NCLT. For voluntary winding up, shareholders can appoint a liquidator by special resolution."
    },
    {
      question: "What happens to employees during winding up?",
      answer: "Employee dues (wages, gratuity, etc.) get priority in distribution. Employees must be paid before unsecured creditors receive anything."
    },
    {
      question: "Can winding up be stopped once started?",
      answer: "Yes, voluntary winding up can be stopped by shareholder resolution before final dissolution. Compulsory winding up requires NCLT permission."
    },
    {
      question: "What are director liabilities during winding up?",
      answer: "Directors may be personally liable for wrongful trading, fraudulent conduct, or breach of duties. Proper winding up protects honest directors."
    }
  ],
  
  relatedServices: [
    { name: "Company Strike Off", slug: "company-strike-off" },
    { name: "LLP Closure", slug: "llp-closure" },
    { name: "Annual Filing", slug: "annual-filing" },
    { name: "ROC Filing", slug: "roc-filing" }
  ],
  
  trustSignals: {
    experience: "12+ Years Experience",
    experts: "CS & NCLT Practitioners",
    closures: "100+ Winding Ups Completed",
    support: "Full Legal Support"
  }
};

export const bisCertification = {
  id: "bis-certification",
  slug: "bis-certification",
  seoTitle: "BIS Certification Services | Bureau of Indian Standards",
  seoDescription: "Get BIS Certification for your products. Mandatory ISI mark for regulated products. Expert assistance with BIS application, testing, and certification.",
  
  heroTitle: "BIS Certification",
  heroSubtitle: "Product Certification",
  
  summary: "BIS (Bureau of Indian Standards) Certification is a quality mark certifying that products conform to Indian Standards. The ISI mark is mandatory for products covered under compulsory certification and indicates quality assurance. Our experts help manufacturers obtain BIS certification through application filing, factory audits, product testing, and compliance management.",
  
  whoNeeds: {
    title: "Who Needs BIS Certification?",
    description: "BIS certification is mandatory for certain products and beneficial for others seeking quality recognition.",
    points: [
      "Manufacturers of products under mandatory certification",
      "Electronics and IT equipment manufacturers",
      "Cement and steel product manufacturers",
      "Electrical equipment and appliance makers",
      "Food product manufacturers (certain categories)",
      "Automotive component manufacturers",
      "Importers of BIS-regulated products",
      "Companies seeking ISI quality mark"
    ]
  },
  
  whyRequired: {
    title: "Why is BIS Certification Important?",
    description: "BIS certification provides quality assurance and market access benefits.",
    points: [
      "Legal requirement for regulated products",
      "Quality assurance for consumers",
      "Market acceptance and trust",
      "Government tender eligibility",
      "Export competitiveness",
      "Liability protection",
      "Brand differentiation",
      "Access to Indian market"
    ]
  },
  
  benefits: [
    {
      title: "Market Access",
      description: "Mandatory certification enables legal sale of regulated products in India"
    },
    {
      title: "Quality Mark",
      description: "ISI mark recognized nationwide as symbol of quality assurance"
    },
    {
      title: "Consumer Trust",
      description: "BIS certification builds consumer confidence in product quality"
    },
    {
      title: "Government Contracts",
      description: "Often required for government procurement and tenders"
    },
    {
      title: "Export Advantage",
      description: "Indian standards increasingly recognized for international trade"
    },
    {
      title: "Legal Protection",
      description: "Certification provides defense against quality-related claims"
    }
  ],
  
  documentsRequired: [
    {
      category: "Company Documents",
      items: [
        "Company registration certificate",
        "PAN and GST registration",
        "Factory license",
        "ISO certification (if available)",
        "Plant layout drawing"
      ]
    },
    {
      category: "Product Documents",
      items: [
        "Product specifications",
        "Manufacturing process details",
        "Quality control plan",
        "Test equipment list",
        "Raw material specifications"
      ]
    },
    {
      category: "Test Reports",
      items: [
        "Product test reports from BIS-recognized lab",
        "Type test certificates",
        "Routine test records",
        "Calibration certificates of testing equipment",
        "Previous certifications (if any)"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Product Assessment",
      description: "Identify applicable Indian Standard for your product and certification scheme (Scheme I, II, or CRS)."
    },
    {
      step: 2,
      title: "Application Filing",
      description: "Prepare and submit online application on BIS portal with all required documents and fees."
    },
    {
      step: 3,
      title: "Document Review",
      description: "BIS reviews submitted documents and may request additional information or clarifications."
    },
    {
      step: 4,
      title: "Product Testing",
      description: "Get products tested at BIS-recognized laboratories as per applicable Indian Standard."
    },
    {
      step: 5,
      title: "Factory Audit",
      description: "BIS officers conduct factory inspection to verify manufacturing capability and quality systems."
    },
    {
      step: 6,
      title: "Grant of License",
      description: "Upon satisfactory audit and test results, BIS grants certification license."
    },
    {
      step: 7,
      title: "Ongoing Compliance",
      description: "Maintain quality systems, submit periodic surveillance samples, and comply with license conditions."
    }
  ],
  
  timeline: {
    total: "60-90 days",
    breakdown: [
      { phase: "Application Processing", duration: "15-20 days" },
      { phase: "Product Testing", duration: "20-30 days" },
      { phase: "Factory Audit", duration: "10-15 days" },
      { phase: "License Grant", duration: "15-25 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹49,999",
    includes: [
      "Application preparation and filing",
      "Document preparation support",
      "BIS coordination",
      "Audit preparation assistance",
      "Test report review",
      "License procurement"
    ],
    governmentFees: "Additional (varies by product)"
  },
  
  faqs: [
    {
      question: "What products require mandatory BIS certification?",
      answer: "Products like cement, steel, electronics, LPG cylinders, electrical appliances, automotive parts, and certain food items require mandatory certification. The list is periodically updated by BIS."
    },
    {
      question: "What is the difference between ISI mark and CRS?",
      answer: "ISI mark (Scheme I) is for Indian manufacturers. CRS (Compulsory Registration Scheme) is for electronics/IT products, often used by importers. Both confirm conformity to Indian Standards."
    },
    {
      question: "How long is BIS certification valid?",
      answer: "BIS license is typically valid for 1-2 years depending on the scheme. Annual renewal is required with surveillance testing and audits."
    },
    {
      question: "Can foreign manufacturers get BIS certification?",
      answer: "Yes, through FMCS (Foreign Manufacturers Certification Scheme) for ISI mark or CRS registration. Factory audit by BIS officers may be conducted overseas."
    },
    {
      question: "What happens if products fail testing?",
      answer: "You can modify the product and resubmit for testing. BIS provides details of non-conformities. Repeated failures may result in application rejection."
    },
    {
      question: "Is factory audit mandatory?",
      answer: "Yes, for ISI mark (Scheme I). For CRS (Scheme II), physical audit may be waived if laboratory test reports are satisfactory, though BIS may still conduct audits."
    }
  ],
  
  relatedServices: [
    { name: "ISO Certification", slug: "iso-certification" },
    { name: "FSSAI License", slug: "fssai-license" },
    { name: "Trademark Registration", slug: "trademark-registration" },
    { name: "IEC Code", slug: "iec-code" }
  ],
  
  trustSignals: {
    experience: "10+ Years BIS Experience",
    experts: "Quality & Compliance Experts",
    certifications: "500+ BIS Certifications",
    support: "End-to-End Support"
  }
};

export const drugLicense = {
  id: "drug-license",
  slug: "drug-license",
  seoTitle: "Drug License Registration | Pharmaceutical Business License",
  seoDescription: "Get Drug License for manufacturing, wholesale, or retail of pharmaceutical products. Expert guidance through CDSCO and state drug authority processes.",
  
  heroTitle: "Drug License Registration",
  heroSubtitle: "Pharmaceutical License",
  
  summary: "Drug License is mandatory for manufacturing, selling, or distributing pharmaceutical products, cosmetics, and medical devices in India. Regulated by CDSCO (Central Drugs Standard Control Organization) and State Drug Authorities, the license ensures that only qualified persons handle drugs under proper conditions. Our experts guide you through the complex licensing process for all drug license categories.",
  
  whoNeeds: {
    title: "Who Needs Drug License?",
    description: "Drug license is mandatory for various pharmaceutical business activities.",
    points: [
      "Pharmaceutical manufacturers",
      "Drug wholesalers and distributors",
      "Retail pharmacies and medical stores",
      "Hospital pharmacies",
      "Cosmetic product manufacturers",
      "Medical device companies",
      "Ayurvedic/homeopathic medicine makers",
      "Drug importers and exporters"
    ]
  },
  
  whyRequired: {
    title: "Why is Drug License Mandatory?",
    description: "Drug licensing ensures public safety and quality control in pharmaceutical trade.",
    points: [
      "Legal requirement for pharmaceutical business",
      "Ensures qualified persons handle medicines",
      "Maintains drug quality standards",
      "Patient safety and public health",
      "Prevention of counterfeit drugs",
      "Enables import/export of pharmaceuticals",
      "Required for government medical supplies",
      "Insurance and liability protection"
    ]
  },
  
  benefits: [
    {
      title: "Legal Operation",
      description: "Operate pharmaceutical business legally without risk of penalties or closure"
    },
    {
      title: "Quality Assurance",
      description: "License ensures adherence to quality standards and storage conditions"
    },
    {
      title: "Market Access",
      description: "Supply to hospitals, government, and institutional buyers"
    },
    {
      title: "Import/Export",
      description: "Enable international pharmaceutical trade with proper licensing"
    },
    {
      title: "Bank Finance",
      description: "Drug license helps secure bank loans for pharmaceutical business"
    },
    {
      title: "Brand Trust",
      description: "Licensed operation builds trust with customers and partners"
    }
  ],
  
  documentsRequired: [
    {
      category: "Business Documents",
      items: [
        "Business registration/incorporation certificate",
        "PAN Card and GST registration",
        "Premises ownership/rent agreement",
        "Site plan and layout",
        "Partnership deed/MOA (as applicable)"
      ]
    },
    {
      category: "Qualification Documents",
      items: [
        "Degree certificate of pharmacist/qualified person",
        "Pharmacy Council registration",
        "Experience certificate",
        "Appointment letter of pharmacist",
        "Affidavit of competent person"
      ]
    },
    {
      category: "Premises & Equipment",
      items: [
        "Premises NOC from landlord",
        "Equipment list (for manufacturing)",
        "Cold storage details (if applicable)",
        "Air conditioning/temperature records",
        "Photographs of premises"
      ]
    }
  ],
  
  process: [
    {
      step: 1,
      title: "Requirement Assessment",
      description: "Identify the type of drug license required based on your business activity (manufacturing/wholesale/retail)."
    },
    {
      step: 2,
      title: "Premises Setup",
      description: "Set up premises meeting space, storage, and equipment requirements as per drug rules."
    },
    {
      step: 3,
      title: "Qualified Person",
      description: "Appoint registered pharmacist or qualified person as required for the license category."
    },
    {
      step: 4,
      title: "Application Filing",
      description: "Submit application to State Drug Authority or CDSCO with all required documents and fees."
    },
    {
      step: 5,
      title: "Inspection",
      description: "Drug inspector visits premises to verify compliance with prescribed standards."
    },
    {
      step: 6,
      title: "License Grant",
      description: "Upon satisfactory inspection, drug license is granted for the applied category."
    }
  ],
  
  timeline: {
    total: "30-60 days",
    breakdown: [
      { phase: "Document Preparation", duration: "7-10 days" },
      { phase: "Application Processing", duration: "10-15 days" },
      { phase: "Premises Inspection", duration: "7-14 days" },
      { phase: "License Issuance", duration: "7-14 days" }
    ]
  },
  
  pricing: {
    startingFrom: "₹19,999",
    includes: [
      "Application preparation",
      "Document verification",
      "Authority coordination",
      "Inspection preparation",
      "License procurement",
      "Compliance guidance"
    ],
    governmentFees: "Additional (varies by state and license type)"
  },
  
  faqs: [
    {
      question: "What are the types of drug licenses?",
      answer: "Main types include: Manufacturing license (Form 25/28), Wholesale license (Form 20B/21B), Retail license (Form 20/21), and Loan license. Each has specific requirements and scope."
    },
    {
      question: "What qualification is needed for drug license?",
      answer: "Retail/wholesale requires registered pharmacist. Manufacturing requires graduate in pharmacy/pharma chemistry with experience. Specific requirements vary by license type."
    },
    {
      question: "How long is drug license valid?",
      answer: "Drug licenses are typically valid for 5 years. Renewal application must be submitted 3 months before expiry to ensure continuity."
    },
    {
      question: "Can I get drug license for online pharmacy?",
      answer: "Online pharmacy licensing is evolving. Currently, you need a valid retail license for the physical premises from which you operate. Additional e-pharmacy regulations may apply."
    },
    {
      question: "What are the premises requirements?",
      answer: "Requirements vary by license type but generally include minimum area, proper storage, temperature control, ventilation, separate storage for controlled substances, and record-keeping systems."
    },
    {
      question: "Is separate license needed for each state?",
      answer: "Retail and wholesale licenses are state-specific. You need separate licenses for operating in different states. Manufacturing license is also state-wise but products can be sold nationwide."
    }
  ],
  
  relatedServices: [
    { name: "FSSAI License", slug: "fssai-license" },
    { name: "GST Registration", slug: "gst-registration" },
    { name: "Shop Establishment", slug: "shop-establishment" },
    { name: "Trade License", slug: "trade-license" }
  ],
  
  trustSignals: {
    experience: "10+ Years Pharma Licensing",
    experts: "Pharma Regulatory Experts",
    licenses: "1,000+ Drug Licenses",
    support: "Pan-India Service"
  }
};
